import { type CylinderSurface, isSameMm, type SizeMm } from '@repo/api-contracts';
import { type KeyboardEvent, type ReactElement, useState } from 'react';
import { measurementFrameOf, type PhotoDimensions, surfaceScale } from '@/entities/edit-area';
import { formatMm } from '@/shared/lib';
import {
  type CylinderInputEdits,
  type CylinderInputField,
  cylinderSurfaceFromInput,
  shownCylinderInput,
} from '../model/cylinder-surface-input';
import { SurfaceAdvancedDetails } from './surface-advanced-details';
import { SurfaceNumberField } from './surface-number-field';

interface CylinderSurfaceInspectorProps {
  readonly surface?: CylinderSurface;
  /** 상품 실측(CYLINDER 면 widthMm 가 지름). 새 표면의 실측은 이 값의 원통 프레임(measurementFrameOf)이다. */
  readonly measurement: SizeMm | null;
  /** 기준 사진 색상의 이 면 사진 크기. 곡률 px ↔ 비율 변환과 축척에 쓴다. 없으면 곡률 칸을 잠근다. */
  readonly photoSize?: PhotoDimensions;
  /** 적용되면 null, 거부되면 그 사유. 사유는 고친 칸 아래에 그대로 보여준다. */
  readonly onApply: (surface: CylinderSurface) => string | null;
  readonly onDirtyChange: (dirty: boolean) => void;
  readonly disabled: boolean;
  /** '고급: 표면 수치 조정' 펼침 여부(표면 패널이 쥔다). 없으면 닫힌 채 시작한다. */
  readonly advancedOpen?: boolean;
  readonly onAdvancedToggle?: (open: boolean) => void;
}

const RANGE_FIELDS = [
  ['left', '보이는 범위 왼쪽 (%)'],
  ['top', '보이는 범위 위 (%)'],
  ['right', '보이는 범위 오른쪽 (%)'],
  ['bottom', '보이는 범위 아래 (%)'],
] as const;
const CURVE_FIELDS = [
  ['topCurve', '위 곡률 (px)'],
  ['bottomCurve', '아래 곡률 (px)'],
] as const;

interface FieldError {
  readonly field: CylinderInputField;
  readonly message: string;
}

/** 적용할 표면이 저장된 표면과 같은가(보이는 사각형·곡률 — 실측·윤곽은 이 폼이 바꾸지 않는다). */
const sameCylinder = (a: CylinderSurface, b: CylinderSurface): boolean =>
  a.topCurveRatio === b.topCurveRatio &&
  a.bottomCurveRatio === b.bottomCurveRatio &&
  a.points.every(
    (point, index) => point.x === b.points[index]?.x && point.y === b.points[index]?.y,
  );

/**
 * 프레임 줄(결정 D-G). 보이는 프레임은 저장 표면의 실측(없으면 상품 실측의 원통 프레임)이고, 지름은 그 프레임이 지금 상품
 * 실측의 원통 프레임과 허용오차 안에서 같을 때만 붙인다 — 실측이 바뀐(drift) 표면에 새 지름을 붙이면 거짓 설명이 된다.
 * 비교는 drift 판정·서버 저장 검사와 같은 계약 isSameMm(GEOMETRY_TOLERANCE)이다.
 */
const frameText = (
  frame: SizeMm | null,
  measured: SizeMm | null,
  measurement: SizeMm | null,
): string => {
  if (frame === null)
    return '상품 실측이 없습니다. 상품 수정 화면에서 지름과 높이를 등록해 주세요.';
  const matchesMeasurement =
    measured !== null &&
    isSameMm(frame.widthMm, measured.widthMm) &&
    isSameMm(frame.heightMm, measured.heightMm);
  const diameter =
    matchesMeasurement && measurement !== null ? ` · 지름 ${formatMm(measurement.widthMm)}` : '';
  return `프레임 ${formatMm(frame.widthMm)} × ${formatMm(frame.heightMm)}mm${diameter}`;
};

/**
 * 원통 표면의 보이는 범위(사진 %)와 위·아래 곡률(기준 사진 px)을 숫자로 맞춘다(결정 R1 숫자 부분·Q1).
 * 고친 칸만 숫자로 바꿔 쓰고, 고치지 않은 칸은 저장된 double 을 그대로 둔다 — 표시 글자를 저장값으로 되돌리지 않는다.
 * 프레임(반둘레 × 높이)은 상품 실측에서 오므로 읽기만 한다.
 * 칸은 Enter 를 누르거나 칸을 떠나면 바로 적용하고(적용 버튼 없음), Esc 는 저장된 값으로 되돌린다. 거부되면 친 글자와
 * 입력 중 표시를 남기고 그 칸 아래에 사유를 보인다. 새 표면은 보이는 범위 네 칸을 다 채울 때까지 칸을 떠나도 적용하지 않는다.
 */
export const CylinderSurfaceInspector = ({
  surface,
  measurement,
  photoSize,
  onApply,
  onDirtyChange,
  disabled,
  advancedOpen,
  onAdvancedToggle,
}: CylinderSurfaceInspectorProps): ReactElement => {
  // 운영자가 고친 칸의 글자. 고친 칸이 있으면 아직 적용되지 않은 입력이 있다는 뜻이다.
  const [edits, setEdits] = useState<CylinderInputEdits>({});
  const pending = Object.keys(edits).length > 0;
  const [error, setError] = useState<FieldError | null>(null);
  // 마지막으로 칸에 맞춘 저장 표면. 적용·사진 위 조작으로 표면이 바뀌면 폼을 다시 만들지 않고 칸만 맞춘다 —
  // 고치지 않은 칸은 저장값에서 다시 보이고, 고치는 중인 칸은 친 글자(edits)를 남긴다.
  const [synced, setSynced] = useState(surface);
  if (synced !== surface) {
    setSynced(surface);
    if (!pending) setError(null);
  }
  const shown = { ...shownCylinderInput(surface, photoSize), ...edits };
  // 상품 실측의 원통 프레임. 새 표면의 실측이 되고, 저장 표면과 비교해 지름 표시 여부를 정한다.
  const measured = measurement === null ? null : measurementFrameOf('CYLINDER', measurement);
  const frame = surface?.realSize ?? measured;
  const scale =
    surface !== undefined && photoSize !== undefined ? surfaceScale(surface, photoSize) : null;
  const edit = (field: CylinderInputField, value: string): void => {
    setEdits({ ...edits, [field]: value });
    onDirtyChange(true);
  };
  const settle = (): void => {
    setError(null);
    setEdits({});
    onDirtyChange(false);
  };

  /** 지금 입력으로 적용한다. 저장된 표면과 같으면 적용하지 않고 입력 중 표시만 지운다. */
  const commit = (field: CylinderInputField, source: 'enter' | 'blur'): void => {
    if (disabled) return;
    // 새 표면은 보이는 범위 네 칸을 차례로 채우는 중이라 칸을 떠날 때마다 사유를 띄우지 않는다.
    if (
      surface === undefined &&
      source === 'blur' &&
      RANGE_FIELDS.some(([key]) => shown[key].trim() === '')
    )
      return;
    const result = cylinderSurfaceFromInput({
      id: surface?.id ?? crypto.randomUUID(),
      surface,
      realSize: frame,
      edits,
      photo: photoSize,
    });
    if (result.surface === undefined) {
      setError({ field, message: result.error });
      return;
    }
    if (surface !== undefined && sameCylinder(result.surface, surface)) {
      settle();
      return;
    }
    const rejection = onApply(result.surface);
    if (rejection !== null) {
      setError({ field, message: rejection });
      return;
    }
    settle();
  };
  const keyHandler =
    (field: CylinderInputField) =>
    (event: KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (pending) commit(field, 'enter');
      } else if (event.key === 'Escape') {
        event.preventDefault();
        settle();
      }
    };
  const alertFor = (field: CylinderInputField): string | null =>
    error?.field === field ? error.message : null;
  const blurHandler = (field: CylinderInputField) => (): void => {
    if (pending) commit(field, 'blur');
  };

  return (
    <form aria-label="원통 표면 입력" noValidate onSubmit={(event) => event.preventDefault()}>
      <fieldset disabled={disabled} className="space-y-3">
        <p className="text-sm">{frameText(frame, measured, measurement)}</p>
        <SurfaceAdvancedDetails open={advancedOpen} onToggle={onAdvancedToggle}>
          <div className="grid grid-cols-2 gap-2">
            {RANGE_FIELDS.map(([field, label]) => (
              <SurfaceNumberField
                key={field}
                label={label}
                value={shown[field]}
                onChange={(value) => edit(field, value)}
                onKeyDown={keyHandler(field)}
                onBlur={blurHandler(field)}
                alert={alertFor(field)}
              />
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {CURVE_FIELDS.map(([field, label]) => (
              <SurfaceNumberField
                key={field}
                label={label}
                value={shown[field]}
                disabled={photoSize === undefined}
                onChange={(value) => edit(field, value)}
                onKeyDown={keyHandler(field)}
                onBlur={blurHandler(field)}
                alert={alertFor(field)}
              />
            ))}
          </div>
          {photoSize === undefined ? (
            <p className="text-sm text-muted-foreground">
              기준 사진 색상의 이 면 사진을 올리면 곡률을 사진 px 로 넣을 수 있습니다.
            </p>
          ) : null}
          {scale !== null ? (
            <p className="text-sm text-muted-foreground">
              가운데 열 기준 축척 가로 {scale.x.toFixed(3)} · 세로 {scale.y.toFixed(3)} px/mm —
              가장자리로 갈수록 가로 축척이 줄어듭니다.
            </p>
          ) : null}
        </SurfaceAdvancedDetails>
      </fieldset>
    </form>
  );
};
